import React from 'react';
import PropTypes from 'prop-types';
import { useWatch, useFormContext } from 'react-hook-form';

import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { useDataProvider, Loading, Error } from 'react-admin';

import { getPlatformIds } from './utils';

import {
    SelectAllArrayInput,
    SELECT_ALL_VALUE,
    CLEAR_ALL_VALUE,
} from '@src/custom/SelectAllArrayInput';
import { EXCLUDED_PRODUCT_LINES } from '@src/custom/ProductsSelectInput';

/**
 * Platform-specific products select component that filters products based on selected platforms.
 */
const PlatformProductsSelect = ({ platformsSource, source, isCreate, ...props }) => {
    const { setValue } = useFormContext();
    const value = useWatch({ name: source });
    const platformsValue = useWatch({ name: platformsSource });

    const [products, setProducts] = React.useState(null);
    const dataProvider = useDataProvider();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const ref = React.useRef();

    // Load products associated with selected platforms when selected platforms are changed
    React.useEffect(() => {
        const platformIds = getPlatformIds(platformsValue);
        if (isEqual(platformIds, ref.current)) return;

        ref.current = platformIds;

        const params = {
            pagination: { page: 1, perPage: 100 },
        };

        if (isCreate && platformIds && platformIds.length > 0) {
            params.filter = { platform_ids: platformIds };
        }

        dataProvider
            .getList('products', params)
            .then(({ data }) => {
                setProducts(data);
                setLoading(false);
                setError(null);
            }).catch((error) => {
                setProducts(null);
                setLoading(false);
                setError(error);
            });
    }, [platformsValue, dataProvider, isCreate]);

    // Update selected products to include only products that are associated with selected platforms
    React.useEffect(() => {
        const safeValue = value || [];
        if (!products) return;
        if (!isCreate) return;
        if (safeValue.length === 0) return;

        // If 'select-all' or 'clear-all' is in value, wait for SelectAllArrayInput to handle it
        if (safeValue.includes(SELECT_ALL_VALUE) || safeValue.includes(CLEAR_ALL_VALUE)) {
            return;
        }

        const productIds = products.map((p) => p.id);
        const filteredProducts = intersection(safeValue, productIds);

        // Only update if the filtered list is different
        const safeValueStrings = safeValue.map(String);
        const filteredStrings = filteredProducts.map(String);

        if (!isEqual(safeValueStrings, filteredStrings)) {
            setValue(source, filteredProducts, { shouldDirty: true, shouldValidate: true });
        }
    }, [products, value, isCreate, source, setValue]);

    if (loading) return <Loading />;
    if (error) return <Error error={error} />;

    return (
        <SelectAllArrayInput
            {...props}
            source={source}
            choices={products}
            groupBy="product_line"
            excludeProductLines={EXCLUDED_PRODUCT_LINES}
        />
    );
};

PlatformProductsSelect.propTypes = {
    source: PropTypes.string.isRequired,
    platformsSource: PropTypes.string.isRequired,
    isCreate: PropTypes.bool,
};

export default React.memo(PlatformProductsSelect);
